<template>
  <div class="event-categories" redesign>
    <!-- Request stepper -->
    <Stepper
      stepInfo="Step 3 of 5"
      description="Describe your situation"
    />

    <div class="event-categories-form form-container">
      <!-- Loading spinner -->
      <div v-if="globalLoading" class="form-field">
        <LoadSpinner :color="`#${organizationDetails.darkBrandColor}`" />
      </div>


      <!-- Display individual event questions -->
      <div class="event-container" v-for="event in events" :key="event.category">
        <RightsEventComponent :event="event" @fieldBlur="saveFieldUpdate" />
      </div>

      <!-- Next step -->
      <div class="form-footer">
        <div class="next-step">
          <translate>Next step:</translate><br /><translate class="next-step-name"
            >Add details</translate
          >
        </div>
      </div>


      <!-- Submit -->
      <SubmitButton :loading="loading" :onContinue="onContinue" />
    </div>
  </div>
</template>

<script>
import EventCategoryComponent from '@/components/requests/EventCategoryComponent'
import Stepper from '@/components/requests/Stepper'
import InfoBox from '@/components/InfoBox'
import Request from '@/services/requests'
import RightsEventComponent from '@/components/requests/RightsEventComponent'
import EventCategory from '@/services/eventCategories'
import Event from '@/services/events/'
import LoadSpinner from '@/components/LoadSpinner'
import { mapState } from 'vuex'
import { analyticsMixin } from '@/services/googleAnalytics'
import RequestStepProgressBar from '@/components/stepper/RequestStepProgressBar'
import SubmitButton from '@/components/requests/SubmitButton'
import Modal from '@/components/Modal'
import Question from '@/components/survey/Question'
import { Promise as bPromise } from 'bluebird'

export default {
  name: 'EventCategories',
  mixins: [analyticsMixin],
  components: {
    EventCategoryComponent,
    LoadSpinner,
    InfoBox,
    RequestStepProgressBar,
    Modal,
    SubmitButton,
    RightsEventComponent,
    Question,
    Stepper,
  },
  data() {
    return {
      eventCategories: [],
      selectedEventCategoryIds: [],
      loading: false,
      globalLoading: false,
      displayCovidError: false,
      displaySelectEmergencyError: false,
      naturalDisasterModalOpen: false,
      events: [],
      content: this.$gettext(
        'Please select the type of emergency or emergencies that made it difficult for you to afford basic living expenses.',
      ),
      BOOLEAN_TEXT_CHOICES: [
        {
          id: 'YES',
          text: this.$gettext('Yes'),
        },
        {
          id: 'NO',
          text: this.$gettext('No'),
        },
        {
          id: "I DON'T KNOW",
          text: this.$gettext("I don't know"),
        },
      ],
    }
  },
  async created() {
    this.globalLoading = true
    // Pull all Event Categories to populate this page
    const response = await EventCategory.api.list({
      filters: { event_category_organization: this.currentRequest.organization },
    })
    this.eventCategories = response.results
    // Auto-select all Event Categories that have been previously selected for this request
    this.selectedEventCategoryIds = this.currentRequest.eventCategories
    this.orderRequestQuestions()
    await this.assembleEvents()
    // this.startEditingPing()
    this.globalLoading = false
  },
  destroyed() {
    clearInterval(this.interval)
  },
  methods: {
    async onContinue() {
      const updateFields = ['questions']

      this.loading = true
      this.trackClickedCTA()

      const promiseList = this.events.map(event => {
        // For each Event that has a populated ID, it already exists - update it
        if (event.id) {
          return Event.api.update(event, updateFields)
          // For each Event that does not, it does not yet exist - create it
        } else {
          return Event.api.create(event)
        }
      })
      // Wait for all Events to be created before updating Request
      await Promise.all(promiseList)

      const request = await Request.api.retrieve(this.currentRequest.id)
      this.$store.dispatch('updateCurrentRequest', request)
    },
    async saveFieldUpdate() {
      let updateFields = []

      updateFields = ['questions']

      const promiseList = this.events.map(event => {
        // For each Event that has a populated ID, it already exists - update it
        if (event.id) {
          // Skip updating events that have incomplete fields
          let incompleteEvent = false
          event.questions.forEach(question => {
            if (!question.answer.text) {
              incompleteEvent = true
            }
          })
          if (incompleteEvent) {
            return 'wrong'
          } else {
            return Event.api.update(event, updateFields)
          }
        }
      })

      await Promise.all(promiseList)

      const request = await Request.api.retrieve(this.currentRequest.id)
      this.$store.dispatch('updateCurrentRequest', request)
    },
    async assembleEvents() {
      // Pull in any existing Events from state.
      // Cast them explicitly as Events, so we get access to their model methods.
      const events = this.currentRequest.eventsRef.map(e => Event.create(e))

      // Find out which of our EventCategories already have an associated Event,
      // and which ones don't yet
      const categoriesWithEvent = events.map(event => event.category)

      const categoriesWithoutEvent = []
      this.currentRequest.eventCategories.forEach(category => {
        if (!categoriesWithEvent.includes(category)) {
          categoriesWithoutEvent.push(category)
        }
      })
      // For each category that doesn't have an associated Event, pull the right info
      // from the API, and create a local Event object
      const categoryInfo = []
      await bPromise.each(categoriesWithoutEvent, async category => {
        categoryInfo.push(await EventCategory.api.retrieve(category))
      })

      categoryInfo.forEach(cat => {
        let event = Event.create({
          category: cat.id,
          categoryRef: cat,
          request: this.currentRequest.id,
          questions: cat.eventQuestions,
        })
        events.push(event)
      })
      // Render our combined list of new and existing Events
      this.events = events
    },
    getSelectionOptions(question) {
      if (question.friendlyName == 'Stimulus Check') {
        return this.BOOLEAN_TEXT_CHOICES
      }
      return this.BOOLEAN_TEXT_CHOICES.slice(0, 2)
    },
    orderRequestQuestions() {
      var last_item = this.currentRequest.requestQuestions.splice(
        this.currentRequest.requestQuestions.findIndex(question => question.order == -1),
        1,
      )
      this.currentRequest.requestQuestions.sort((first, second) => first.order - second.order)
      this.currentRequest.requestQuestions = this.currentRequest.requestQuestions.concat(last_item)

      // If multiple choice, add answer options for the Questions component.
      for (var index in this.currentRequest.requestQuestions) {
        var requestQuestion = this.currentRequest.requestQuestions[index]
        if (requestQuestion.qType == 'MULTIPLE_CHOICE') {
          this.currentRequest.requestQuestions[index]['answerOptions'] = this.getSelectionOptions(
            requestQuestion,
          )
          this.currentRequest.requestQuestions[index].isText = true
        }
      }
    },
    addEventCategory(eventCategory) {
      const val = eventCategory.id
      this.displaySelectEmergencyError = false
      this.eventsTracker('selected-event-category', `Category ID: ${val}`)
      // User not allowed to select both "Other" and a different category - so first remove Other
      this.removeOtherFromSelected()
      this.selectedEventCategoryIds.push(val)
    },
    async removeEventCategory(eventCategory) {
      const val = eventCategory.id
      this.eventsTracker('deselected-event-category', `Category ID: ${val}`)
      this.selectedEventCategoryIds = this.selectedEventCategoryIds.filter(id => id != val)

      this.currentRequest.eventsRef.forEach(async oldEvent => {
        if (oldEvent.categoryRef.id == val) {
          await this.$store.dispatch('partialUpdateEvent', { id: oldEvent.id, removeEvent: true })
        }
      })
    },
    // reducer function to get the id of COVID-19
    getCOVIDId() {
      const value = this.eventCategories.reduce((returnedValue, event) => {
        if (event.name === EventCategory.options.COVID19) {
          returnedValue = event.id
          return returnedValue
        } else {
          return returnedValue
        }
      }, '')
      return value
    },
    onOtherCategorySelected() {
      this.eventsTracker('selected-event-category', `Category ID: ${this.otherCategory.id}`)
      this.selectedEventCategoryIds = [this.otherCategory.id]
      this.onContinue()
    },
    removeOtherFromSelected() {
      if (this.otherCategory && this.selectedEventCategoryIds.includes(this.otherCategory.id)) {
        this.eventsTracker('deselected-event-category', `Category ID: ${this.otherCategory.id}`)
        this.selectedEventCategoryIds = this.selectedEventCategoryIds.filter(
          id => id !== this.otherCategory.id,
        )
      }
    },
    startEditingPing() {
      const request = this.currentRequest
      Request.api.editingPing(request)
      this.interval = setInterval(function() {
        Request.api.editingPing(request)
      }, 10000)
    },
  },
  computed: {
    ...mapState(['user', 'currentRequest', 'organizationDetails']),
    disabled() {
      // Disable button if user hasn't selected any categories, or if page is loading
      if (this.selectedEventCategoryIds.length == 0 || this.loading) {
        return true
      }
      return false
    },
    /* Return all selectable event categories - i.e. all except the public category "Other". */
    selectableEventCategories() {
      const allButOther = this.eventCategories.filter(
        eC => eC.name.toLowerCase() !== 'other' && eC.name.toLowerCase() !== 'otra',
      )
      return allButOther
    },
    /* Return public event category "Other", if it is available for this organization; otherwise return null. */
    otherCategory() {
      const otherCategory = this.eventCategories.find(
        eC => eC.name.toLowerCase() === 'other' || eC.name.toLowerCase() === 'otra',
      )
      return otherCategory ? otherCategory : null
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/responsive';

.event-categories {
  display: flex;
  flex-direction: column;

  .event-categories-form {
    @extend .constrain-tablet-and-up;
  }
}
</style>
